import { useState } from "react";
import { PropTypes } from "prop-types";
import { API } from "aws-amplify";
import { Button, TextField } from "@mui/material";
import {
  createAlumniProfile,
  updateAlumniProfile,
} from "../../graphql/mutations";
import Skills from "../Skills/Skills";
import "./alumForm.scss";
import { sparseIndexTrue } from "../../constants/enums";
import LocationFields from "../FormComponents/LocationFields";
import CohortsDropdown from "../FormComponents/CohortsDropdown";
import ServiceBranchDropdown from "../FormComponents/ServiceBranchDropdown";
import CheckboxControl from "../FormComponents/CheckboxControl";

const propTypes = {
  cohorts: PropTypes.objectOf(PropTypes.any).isRequired,
  alumniProfile: PropTypes.objectOf(PropTypes.any).isRequired,
  setAlumniProfile: PropTypes.func.isRequired,
  setIsEditMode: PropTypes.func.isRequired,
  setCreateProfile: PropTypes.func.isRequired,
};

const AlumForm = ({
  cohorts,
  alumniProfile,
  setAlumniProfile,
  setIsEditMode,
  setCreateProfile,
}) => {
  const [firstNameDisplay, setFirstNameDisplay] = useState(
    alumniProfile.firstNameDisplay || ""
  );
  const [lastNameDisplay, setLastNameDisplay] = useState(
    alumniProfile.lastNameDisplay || ""
  );
  const [about, setAbout] = useState(alumniProfile.about || "");
  const [isPublicProfile, setIsPublicProfile] = useState(
    typeof alumniProfile.publicProfile === "undefined"
      ? true
      : Boolean(alumniProfile.publicProfile)
  );
  const [isWillingToTravel, setIsWillingToTravel] = useState(
    alumniProfile.isWillingToTravel || false
  );
  const [isOpenToWork, setIsOpenToWork] = useState(
    alumniProfile.isOpenToWork || false
  );
  const [isMilitarySpouse, setIsMilitarySpouse] = useState(
    alumniProfile.isMilitarySpouse || false
  );
  const [skills, setSkills] = useState(alumniProfile.skills || []);
  const [email, setEmail] = useState(alumniProfile.email || "");
  const [linkedInUrl, setLinkedInUrl] = useState(
    alumniProfile.linkedInUrl || ""
  );
  const [cohortID, setCohortID] = useState(alumniProfile.cohortID || "");
  const [branch, setBranch] = useState(alumniProfile.branch || "");
  const [location, setLocation] = useState({
    countryCode: alumniProfile.countryCode || "",
    stateCode: alumniProfile.stateCode || "",
    cityName: alumniProfile.cityName || "",
  });

  const buildProfileFromState = () => {
    return {
      firstName: firstNameDisplay.toLowerCase().replace(/\s/g, ""),
      firstNameDisplay,
      lastName: lastNameDisplay.toLowerCase().replace(/\s/g, ""),
      lastNameDisplay,
      about,
      publicProfile: isPublicProfile ? sparseIndexTrue : null,
      isWillingToTravel,
      isOpenToWork,
      isMilitarySpouse,
      skills: skills.map((skill) => skill.toLowerCase()),
      email: email || null,
      linkedInUrl,
      cohortID,
      branch,
      countryCode: location.countryCode,
      stateCode: location.stateCode,
      cityName: location.cityName,
    };
  };

  const updateProfileStateFields = (profile) => {
    setFirstNameDisplay(profile.firstNameDisplay);
    setLastNameDisplay(profile.lastNameDisplay);
    setAbout(profile.about);
    setIsPublicProfile(Boolean(profile.publicProfile));
    setIsWillingToTravel(profile.isWillingToTravel);
    setIsOpenToWork(profile.isOpenToWork);
    setIsMilitarySpouse(profile.isMilitarySpouse);
    setSkills(profile.skills);
    setEmail(profile.email || "");
    setLinkedInUrl(profile.linkedInUrl);
    setCohortID(profile.cohortID);
    setBranch(profile.branch);
    setLocation({
      countryCode: profile.countryCode,
      stateCode: profile.stateCode,
      cityName: profile.cityName,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let returnedProfile;
    if (alumniProfile.id) {
      const input = { ...buildProfileFromState(), id: alumniProfile.id };
      returnedProfile = await API.graphql({
        query: updateAlumniProfile,
        variables: { input },
      });
      returnedProfile = returnedProfile.data?.updateAlumniProfile;
    } else {
      returnedProfile = await API.graphql({
        query: createAlumniProfile,
        variables: { input: buildProfileFromState() },
      });
      returnedProfile = returnedProfile.data?.createAlumniProfile;
    }
    if (returnedProfile) {
      setAlumniProfile(returnedProfile);
      updateProfileStateFields(returnedProfile);
      setCreateProfile(false);
      setIsEditMode(false);
    } else {
      alert("Failed to save profile. Please try again");
    }
  };

  return (
    <form className="alum-form" onSubmit={handleSubmit}>
      <div className="form-title">Edit Profile</div>
      <TextField
        id="first-name"
        className="text-field"
        variant="outlined"
        label="First name"
        value={firstNameDisplay}
        onChange={(e) => setFirstNameDisplay(e.target.value)}
        required
      />
      <TextField
        id="last-name"
        className="text-field"
        variant="outlined"
        label="Last name"
        value={lastNameDisplay}
        onChange={(e) => setLastNameDisplay(e.target.value)}
        required
      />
      <TextField
        id="email"
        type="email"
        className="text-field"
        variant="outlined"
        label="Email Address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        id="linkedIn"
        className="text-field"
        variant="outlined"
        label="Your LinkedIn profile url"
        value={linkedInUrl}
        onChange={(e) => setLinkedInUrl(e.target.value)}
      />
      <CohortsDropdown
        cohorts={cohorts}
        value={cohortID}
        onChange={(e) => setCohortID(e.target.value)}
      />
      <ServiceBranchDropdown
        value={branch}
        onChange={(e) => setBranch(e.target.value)}
      />
      <LocationFields location={location} setLocation={setLocation} />
      <TextField
        id="about"
        className="text-field"
        label="About"
        value={about}
        multiline
        rows={4}
        variant="outlined"
        placeholder="About"
        onChange={(e) => setAbout(e.target.value)}
      />
      <CheckboxControl
        label="Set profile to public?"
        checked={isPublicProfile}
        onChange={(e) => setIsPublicProfile(e.target.checked)}
      />
      <CheckboxControl
        label="Military Spouse?"
        checked={isMilitarySpouse}
        onChange={(e) => setIsMilitarySpouse(e.target.checked)}
      />
      <CheckboxControl
        label="Willing to Travel?"
        checked={isWillingToTravel}
        onChange={(e) => setIsWillingToTravel(e.target.checked)}
      />
      <CheckboxControl
        label="Open to work?"
        checked={isOpenToWork}
        onChange={(e) => setIsOpenToWork(e.target.checked)}
      />
      <Skills skills={skills} setSkills={setSkills} />
      <div className="buttons">
        <Button
          className="form-button submit"
          variant="contained"
          color="primary"
          type="submit"
        >
          Save
        </Button>
        <Button
          className="form-button cancel"
          variant="outlined"
          color="primary"
          onClick={() => setIsEditMode(false)}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

AlumForm.propTypes = propTypes;

export default AlumForm;
